/**
 * Created by Ella Ma on 02/09/2021.
 * Description:
 *
 * ------ maintenance history ------
 * 9/23/2021 Simon Zhao added a config section for notification setting.
 */

import { Component, OnInit, OnDestroy, Input, ViewChild } from '@angular/core';
import { createSelector, select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import { catchError, map, take, filter, debounceTime } from 'rxjs/operators';
import { AppState } from '../../../redux';
import { relationshipTypeSelector } from '../../../redux/reducers/relationship-type.reducer';
import { FieldBrief, Parent, PostAction, PostNotification, Relationship, TaskType } from '../../../tamalelibs/models/workflow.model';
import { EntityService } from '../../../tamalelibs/services/entity.service';
import { SingleEntityDropdownFilterConfig } from '../../../widgets/single-entity-dropdown-filter/single-entity-dropdown-filter.model';
import { SingleSelectDropdownFilterConfig } from '../../../widgets/single-select-dropdown-filter/single-select-dropdown-filter.model';
import { PostNotificationFieldEnum, PostActionConfig, CustomValidators } from './task-post-action.model';
import { ControlType } from '../../../tamalelibs/models/template-control.model';
import { TaskDefinitionConfig } from '../task-definition/task-definition.model';
import { currentWorkflowFieldsSelector } from '../../../tamalelibs/redux/reducers/workflow.reducer';
import { WorkflowActionTypes } from '../../../tamalelibs/redux/actions/workflow.actions';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { ArrayHelperService } from '../../../tamalelibs/services/array-helper.service';
import { ParticipantType } from './task-post-action.model';
import { Subject } from 'rxjs';
import { UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
export const ENTITY_DEFAULT_VALUE = 'Select an entity';
export const PARENT_ENTITY_CONTROL_DEFAULT_VALUE = 'Select the parent entity control';
export const RELATIONSHIP_TYPE_DEFAULT_VALUE = 'Select a relationship';

@Component({
    selector: 'task-post-action',
    templateUrl: './task-post-action.component.html',
    styleUrls: ['./task-post-action.component.scss']
})
export class TaskPostActionComponent implements OnInit, OnDestroy {
    @Input() config: TaskDefinitionConfig;
    @Input() otherConfig: PostActionConfig;
    @ViewChild('kendom', { static: false }) kendom;


    childEntity = ENTITY_DEFAULT_VALUE;
    childEntityConfig: SingleEntityDropdownFilterConfig = new SingleEntityDropdownFilterConfig(true);
    /**
     * the entity list with filters applied.
     *
     * @type {Array<any>}
     * @memberof TaskPostActionComponent
     */
    filteredEntityItems: Array<any> = [];

    parentEntityControlConfig: SingleSelectDropdownFilterConfig = new SingleSelectDropdownFilterConfig(true);
    parentEntityControlLableName = PARENT_ENTITY_CONTROL_DEFAULT_VALUE;
    relationshipType = RELATIONSHIP_TYPE_DEFAULT_VALUE;
    relationshipTypeConfig: SingleSelectDropdownFilterConfig = new SingleSelectDropdownFilterConfig(true);
    selectedValues: Array<{ id: string, name: string, type: string }> = [];
    /**
     * a flag indicating whether the entity list is loading.
     *
     * @memberof TaskPostActionComponent
     */
    isListLoading = false;

    get notificationFrmGroup() {
        return this.otherConfig && this.otherConfig.notificationValidationFrmGroup;
    }

    get isEntityErrorMsgVisible(): boolean {
        return this.notificationFrmGroup.dirty && this.notificationFrmGroup.get(PostNotificationFieldEnum.fixedReceivers).invalid;
    }

    get isCheckboxErrorMsgVisible(): boolean {
        return this.notificationFrmGroup.dirty && this.notificationFrmGroup.get(PostNotificationFieldEnum.email).invalid;
    }
    valid = true;

    /**
     * The full set of candidates for recievers dropdown list
     *
     * @type {Array<any>}
     * @memberof TaskPostActionComponent
     */
    private _allReceiverEntityItems: Array<any> = [];

    private _destroySubscriptions: Array<Subscription> = [];

    private _filterSubject$: Subject<string> = new Subject();

    constructor(
        private _entityService: EntityService,
        private _store: Store<AppState>,
        private _storeQueriery: StoreQuerierService,
    ) { }

    static syncBetweenConfigAndFrmGroup(notification: PostNotification, frmGroup: UntypedFormGroup, isPristine: boolean = false) {
        const syncSingleFieldFunc = (field: PostNotificationFieldEnum, v: any, validator?: ValidatorFn) => {
            if (!frmGroup.contains(field)) {
                const ctrl = new UntypedFormControl(v, validator);
                frmGroup.addControl(field, ctrl);
                return true;
            } else {
                frmGroup.get(field).setValue(v);
                if (!isPristine) {
                    frmGroup.markAsDirty();
                }
                return false;
            }
        };

        syncSingleFieldFunc(PostNotificationFieldEnum.fixedReceivers, notification && notification.fixedReceivers, Validators.required);
        syncSingleFieldFunc(PostNotificationFieldEnum.email, notification && notification.email);
        if (syncSingleFieldFunc(PostNotificationFieldEnum.inApp, notification && notification.inApp)) {
            // ensure the group validator only sets once.
            frmGroup.setValidators(CustomValidators.groupBooleanRequired(PostNotificationFieldEnum.email, PostNotificationFieldEnum.inApp));
        }
    }

    filterReceivers(e: any) {
        this.isListLoading = true;
        this._filterSubject$.next(e);
    }

    focusRecieverList(): void {
        this.kendom.toggle(true);
    }

    ngOnInit(): void {
        this._initCreateRelationship();
        this._initListData();
    }

    ngOnDestroy(): void {
        this._initValid();
    }

    onReceiverSelectionChanged(event: any): void {
        const selectedEntityIds = [];
        this.selectedValues.forEach(item => {
            selectedEntityIds.push(item.id);
        });

        this.config.taskDefinition.postNotification.fixedReceivers = selectedEntityIds;
        this._syncNotificationFrmGroup(this.config.taskDefinition.postNotification);
    }

    onChildEntityItemClick(event) {
        this.childEntityConfig.open$.next(event.target);
    }

    onCreateEntity() {

    }

    onCreateNote() {

    }

    onParentEntityControlItemClick(event) {
        this.parentEntityControlConfig.open$.next(event.target);
    }

    onRelationshipTypeItemClick(event) {
        this.relationshipTypeConfig.open$.next(event.target);
    }

    onCreateRelationship() {
        this.config.configChanged = true;
        if (!this.otherConfig.checkCreateRelationship) {
            this.config.taskDefinition.postAction.relationship = new Relationship();
        } else {
            this._initValid();
        }
    }

    onUpdateMetadata() {

    }

    onToggleNotification() {
        if (this.otherConfig.isNotificationOn) {
            this.config.taskDefinition.postNotification = new PostNotification();
            this.notificationFrmGroup.reset();
            this.selectedValues = [];
            this._syncNotificationFrmGroup(this.config.taskDefinition.postNotification);
        } else {
            delete this.config.taskDefinition.postNotification;
        }
    }

    onEmailOptionChanged() {
        this._syncNotificationFrmGroup(this.config.taskDefinition.postNotification);
    }

    onInAppOptionChanged() {
        this._syncNotificationFrmGroup(this.config.taskDefinition.postNotification);
    }

    private _initListData(): void {
        const accessListSelector = createSelector(
            (state: AppState) => state.users,
            (state: AppState) => state.teams,
            (allUser, allTeam) => {
                if (allUser.length === 0 && allTeam.length === 0) {
                    return [];
                }
                const users = allUser.map(item => {
                    return {
                        type: ParticipantType.user,
                        ...item
                    };
                });
                const teams = allTeam.map(item => {
                    return {
                        type: ParticipantType.team,
                        ...item
                    };
                });
                return users.concat(teams);
            }
        );
        this._destroySubscriptions.push(
            this._store.pipe(
                select(accessListSelector),
                filter((list: any) => list.length > 0),
            ).subscribe(list => {
                ArrayHelperService.sort<any>(list, 'name');
                this.filteredEntityItems = this._allReceiverEntityItems = list;
                this.isListLoading = false;
            }),
            this._filterSubject$.pipe(
                debounceTime(250),
            ).subscribe(str => {
                if (str) {
                    this.filteredEntityItems = this._allReceiverEntityItems.filter(item => {
                        if (item.name.toLowerCase().indexOf(str.toLowerCase()) > -1) { return item; }
                        if (item.shortName.toLowerCase().indexOf(str.toLowerCase()) > -1) { return item; }
                        if (item.aliases.filter(value => value.toLowerCase().indexOf(str.toLowerCase()) > -1).length > 0) { return item; }
                    });
                } else {
                    this.filteredEntityItems = this._allReceiverEntityItems;
                }
                this.isListLoading = false;
            }),
        );


        let notification = new PostNotification();
        if (PostNotification.isPostNotificationOn(this.config.taskDefinition.postNotification)) {
            this.filteredEntityItems.forEach((data, index) => {
                if (this.config.taskDefinition.postNotification.fixedReceivers.indexOf(data.id) > -1) {
                    this.selectedValues.push(data);
                }
            });
            notification = this.config.taskDefinition.postNotification;
        }

        this._syncNotificationFrmGroup(notification);
    }

    private _syncNotificationFrmGroup(notification: PostNotification, isPristine: boolean = false) {
        TaskPostActionComponent.syncBetweenConfigAndFrmGroup(notification, this.notificationFrmGroup, isPristine);
    }

    private _initChildEntity() {
        this._destroySubscriptions.push(
            this.childEntityConfig.onChangeValue$.subscribe(data => {
                if (data) {
                    this.config.configChanged = true;
                    this.childEntity = data.shortName;
                    this.config.taskDefinition.postAction.relationship.child = data.id;

                    this._initValid();
                }
            }),
        );

        if (this.config.taskDefinition.postAction.relationship && this.config.taskDefinition.postAction.relationship.child) {
            const childEntityId = this.config.taskDefinition.postAction.relationship.child;
            return this._entityService.getEntityDetail(childEntityId).pipe(
                catchError(() => {
                    return of(null);
                }),
                take(1),
                map((response) => {
                    if (response) {
                        this.childEntity = response['short-name'];
                    }
                    return response;
                })
            );
        }
        return of(this.childEntity);
    }

    private _initCreateRelationship(): void {
        // init create relationship checkbox
        if (!this.config.taskDefinition.postAction) {
            this.config.taskDefinition.postAction = new PostAction();
        }
        // update the current fields in store
        const currentFieldBriefs = [];
        if (this.config.taskDefinition.type === TaskType.INPUT) {
            this.config.taskDefinition.template.contains.forEach(row => {
                row.forEach(control => {
                    if (control.type === ControlType.SINGLE_ENTITY_DROP_DOWN ||
                        control.type === ControlType.MULTI_ENTITY_DROP_DOWN ||
                        control.type === ControlType.SINGLE_USER_DROP_DOWN) {
                        const fieldBrief = FieldBrief.parse(control);
                        fieldBrief.taskDefinitionId = this.config.taskDefinition.id;
                        currentFieldBriefs.push(fieldBrief);
                    }
                });
            });
        }
        this._store.dispatch({
            type: WorkflowActionTypes.UPDATE_CURRENT_WORKFLOW_FIELDS,
            payload: {
                taskDefinitionId: this.config.taskDefinition.id,
                changedFields: currentFieldBriefs
            }
        });


        this._initParentEntityControl();
        this._initRelationType();
        this._initChildEntity().subscribe(() => {
            this._initValid();
        });
    }

    private _initValid() {
        // create relationship
        this._verifyParentControl();
        if (this.otherConfig.checkCreateRelationship) {
            if (this.parentEntityControlLableName !== PARENT_ENTITY_CONTROL_DEFAULT_VALUE &&
                this.relationshipType !== RELATIONSHIP_TYPE_DEFAULT_VALUE &&
                this.childEntity !== ENTITY_DEFAULT_VALUE) {
                this.valid = true;
            } else {
                this.valid = false;
            }
        } else {
            this.valid = true;
        }
    }

    private _initRelationType(): void {
        let selectedRelationshipTypeId;
        if (this.config.taskDefinition.postAction.relationship) {
            selectedRelationshipTypeId = this.config.taskDefinition.postAction.relationship.type;
        }
        this._store.pipe(select(relationshipTypeSelector
        ),
            take(1)
        ).subscribe(res => {
            if (res && res.length > 0) {
                res.forEach(item => {
                    this.relationshipTypeConfig.data.push({
                        id: item.id,
                        name: item.name,
                        selected: selectedRelationshipTypeId === item.id,
                    });
                });
            }
        });

        this._destroySubscriptions.push(
            this.relationshipTypeConfig.onChangeValue$.subscribe(data => {
                if (data) {
                    this.config.configChanged = true;
                    this.relationshipType = data.name;
                    this.config.taskDefinition.postAction.relationship.type = data.id;

                    this._initValid();
                }
            }),
        );

        // set relationship
        if (this.config.taskDefinition.postAction.relationship && this.config.taskDefinition.postAction.relationship.type) {
            const relationshipType = this.config.taskDefinition.postAction.relationship.type;
            this.relationshipTypeConfig.data.filter(item => {
                if (item.id === relationshipType) {
                    this.relationshipType = item.name;
                }
            });
        }
    }

    private _initParentEntityControl(): void {
        let selectedParentEntityControlId;
        if (this.config.taskDefinition.postAction.relationship && this.config.taskDefinition.postAction.relationship.parent) {
            selectedParentEntityControlId = this.config.taskDefinition.postAction.relationship.parent.controlId;
        }
        this._destroySubscriptions.push(
            this._store.pipe(
                select(currentWorkflowFieldsSelector),
            ).subscribe(fieldBriefs => {
                if (fieldBriefs && fieldBriefs.length) {
                    fieldBriefs.forEach(item => {
                        const index = this.parentEntityControlConfig.data.findIndex(res => res.id === item.id);
                        if (index === -1) {
                            this.parentEntityControlConfig.data.push({
                                id: item.controlId,
                                name: item.controlName,
                                selected: selectedParentEntityControlId === item.controlId
                            });
                        }
                    });
                    // set value for parent entity control
                    const parentControlId = this.config.taskDefinition.postAction.relationship.parent.controlId;
                    let controlId = '';
                    fieldBriefs.forEach(control => {
                        // create or edit
                        if (control.controlId === parentControlId) {
                            controlId = control.controlId;
                        }
                    });
                    if (controlId) {
                        this.parentEntityControlConfig.data.filter(item => {
                            if (item.id === controlId) {
                                this.parentEntityControlLableName = item.name;
                            }
                        });
                    } else {
                        this.config.taskDefinition.postAction.relationship.parent = new Parent();
                    }
                }
            })
        );

        // register value change event
        this._destroySubscriptions.push(
            this.parentEntityControlConfig.onChangeValue$.subscribe(data => {
                if (data) {
                    this.config.configChanged = true;
                    this.parentEntityControlLableName = data.name;
                    this.config.taskDefinition.postAction.relationship.parent.fieldDefId = data.id;
                    this.config.taskDefinition.postAction.relationship.parent.controlId = data.id;

                    this._initValid();
                }
            }),
        );
    }

    private _verifyParentControl(): void {
        const postAction = this.config.taskDefinition.postAction;
        let isUpdated = false;
        if (postAction && postAction.relationship && postAction.relationship.parent) {
            const fieldBriefsInStore = this._storeQueriery.queryBySelector(currentWorkflowFieldsSelector);
            if (fieldBriefsInStore) {
                fieldBriefsInStore.forEach(field => {
                    if (field.controlId === postAction.relationship.parent.controlId) {
                        this.config.taskDefinition.postAction.relationship.parent.fieldDefId = field.fieldId;
                        isUpdated = true;
                    }
                });
            }

            if (!isUpdated) {
                this.config.taskDefinition.postAction.relationship.parent = new Parent();
            }
        }
    }
}
